import {
  addFilterParams,
  GridColumn,
  GridLoader,
  GridLoadSuccess,
  GridLoadTotalSuccess,
  GridResultLoader,
  GridResultOptions,
  gridResultsParams,
  GridRow,
  GridSort,
  GridTotalLoader,
  GridTotalsGridLoader,
  GridTotalsLoadGridSuccess,
} from "@getregistered/greg-editors";
import { fetchJson } from "../../components/railsFetch";

export const addSortParams = (params: URLSearchParams, sort: GridSort) => {
  params.append("s", sort.column);
  params.append("so", sort.order);
};

export type GridLoadSuccessResponse = Omit<GridLoadSuccess, "type">;

// The nested filter hash the server-side grids parse: { f: { col: ... } }.
// Selection payloads (bulk operations, selection exports) travel as JSON, so
// they must carry this shape — flat "f[col][]" keys never get Rack's
// query-string parsing and silently apply no filter at all (GR-2206).
export type NestedFilterParams = Record<
  string,
  Record<string, string | string[] | Record<string, string>>
>;

// Serializes active filters exactly as the results fetch does (via the
// package's own addFilterParams), then folds the query entries into the
// nested hash. Going through addFilterParams keeps one invariant: a bulk
// operation filters precisely like the grid the user was looking at —
// including repeated values for multi-choice filters, which the flat
// SelectionStatePayload.filterParams record collapses.
export const nestedFilterParams = (
  columns: GridColumn[],
  filters: GridResultOptions["filters"]
): NestedFilterParams => {
  const params = new URLSearchParams();
  addFilterParams(params, columns, filters);

  const f: Record<string, string | string[] | Record<string, string>> = {};
  for (const [key, value] of params.entries()) {
    const match = key.match(/^f\[([^\]]+)\](?:\[([^\]]*)\])?$/);
    if (!match) continue;
    const [, column, sub] = match;
    if (sub === undefined) {
      f[column] = value;
    } else if (sub === "") {
      const existing = f[column];
      f[column] = Array.isArray(existing) ? [...existing, value] : [value];
    } else {
      const existing = f[column];
      const range = typeof existing === "object" && !Array.isArray(existing) ? existing : {};
      f[column] = { ...range, [sub]: value };
    }
  }
  return Object.keys(f).length > 0 ? { f } : {};
};

// GR-3701: a grid's `filters` from the server are its default filters
// (GregGrid default_filters), rendered by greg-editors as the standard filter
// chip. When the user clears that chip greg-editors deletes the key from
// state, and addFilterParams then sends nothing for the column, so the server
// would apply the default again and the chip could never be cleared. Every
// default the state no longer carries goes out as an explicit blank, which
// GregGrid reads as "cleared". Only scalar and single-choice columns: a range
// with no min or max sends nothing, and permit drops a blank scalar for a
// multi-choice key, so neither has a cleared form the server would accept.
export const markClearedDefaults = (
  columns: GridColumn[],
  defaultFilters: GridResultOptions["filters"],
  filters: GridResultOptions["filters"]
): GridResultOptions["filters"] => {
  if (defaultFilters === undefined || Object.keys(defaultFilters).length === 0) {
    return filters;
  }
  const marked: NonNullable<GridResultOptions["filters"]> = { ...(filters ?? {}) };
  for (const key of Object.keys(defaultFilters)) {
    if (key in marked) continue;
    const column = columns.find((c) => c.key === key);
    if (column === undefined) continue;
    if (column.choices !== undefined && !column.singleChoiceFilter) continue;
    if (column.type === "date" || column.type === "integer" || column.type === "currency") continue;
    marked[key] = "";
  }
  return marked;
};

export const fetchGridResults = async (
  baseUrl: string,
  columns: GridColumn[],
  params: GridResultOptions,
  signal: AbortSignal
): Promise<any> => {
  const queryParams = new URLSearchParams({
    p: params.pageParams.page.toString(),
    pp: params.pageParams.perPage.toString(),
  });
  if (params.sort !== undefined) {
    addSortParams(queryParams, params.sort);
  }
  addFilterParams(queryParams, columns, params.filters);
  const separator = baseUrl.includes("?") ? "&" : "?";
  const url = `${baseUrl}${separator}${queryParams.toString()}`;
  const response = await fetchJson(url, { signal });
  return await response.json();
};

export const resultLoader = <TResponse extends GridLoadSuccessResponse>(
  baseUrl: string,
  columns: GridColumn[],
  onFetchResults?: (response: TResponse) => void
): GridResultLoader => {
  return async ({ pageParams, filters, sort }, signal) => {
    const result = (await fetchGridResults(
      baseUrl,
      columns,
      { pageParams, filters, sort },
      signal
    )) as TResponse;
    onFetchResults?.(result);
    return {
      type: "success",
      totalResults: result.totalResults as number,
      rows: result.rows as GridRow[],
    } as GridLoadSuccess;
  };
};

export const totalsGridLoader = (
  baseUrl: string | undefined,
  columns: GridColumn[]
): GridTotalsGridLoader => {
  return async ({ column, filters }, signal) => {
    if (!baseUrl) {
      throw new Error("totalsGridLoader called without a totals URL; this grid has no totals row configured");
    }
    const params = new URLSearchParams({ column: column.key, mode: "grid" });
    addFilterParams(params, columns, filters);

    const separator = baseUrl.includes("?") ? "&" : "?";
    const url = `${baseUrl}${separator}${params.toString()}`;
    const response = await fetchJson(url, { signal });
    const result = await response.json();
    return {
      type: "success",
      grid: result.grid,
      rows: result.rows,
    } as GridTotalsLoadGridSuccess;
  };
};

export const totalLoader = (
  baseUrl: string | undefined,
  columns: GridColumn[]
): GridTotalLoader => {
  return async ({ column, filters }, signal) => {
    if (!baseUrl) {
      throw new Error("totalLoader called without a totals URL; this grid has no totals row configured");
    }
    const params = new URLSearchParams({ column: column.key, mode: "simple" });
    addFilterParams(params, columns, filters);

    const separator = baseUrl.includes("?") ? "&" : "?";
    const url = `${baseUrl}${separator}${params.toString()}`;
    const response = await fetchJson(url, { signal });
    const result = await response.json();

    return {
      type: "success",
      totals: result.totals,
    } as GridLoadTotalSuccess;
  };
};

export const loadRecordsKeyGenerator = (
  resultsUrl: string,
  columns: GridColumn[]
) => {
  return (options: GridResultOptions) => {
    const separator = resultsUrl.includes("?") ? "&" : "?";
    return `${resultsUrl}${separator}${gridResultsParams(columns, options)}`;
  };
};

export const gridLoader = <
  TLoadRecordsResponse extends GridLoadSuccessResponse = GridLoadSuccessResponse
>(
  resultsUrl: string,
  totalsUrl: string | undefined,
  columns: GridColumn[],
  onFetchResults?: (response: TLoadRecordsResponse) => void
): GridLoader => {
  return {
    loadRecords: resultLoader<TLoadRecordsResponse>(
      resultsUrl,
      columns,
      onFetchResults
    ),
    loadRecordsKey: loadRecordsKeyGenerator(resultsUrl, columns),
    loadTotal: totalLoader(totalsUrl, columns),
    loadTotalsGrid: totalsGridLoader(totalsUrl, columns),
  };
};
